import React from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { Scale, Sparkles, ShieldCheck, Handshake } from "lucide-react";
import PageHero from "@/components/shared/PageHero";
import { useLang } from "@/i18n/LanguageContext";

const HERO_IMG =
  "https://images.pexels.com/photos/32168739/pexels-photo-32168739.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1600";

const PRINCIPLE_ICONS = [Scale, Sparkles, ShieldCheck, Handshake];

export default function Groupe() {
  const { t } = useLang();

  return (
    <div data-testid="groupe-page">
      <PageHero
        overline={t.groupe.hero.overline}
        title={t.groupe.hero.title}
        titleItalic={t.groupe.hero.titleItalic}
        subtitle={t.groupe.hero.subtitle}
        image={HERO_IMG}
      />

      {/* Vision & Mission */}
      <section className="bg-ekip-cream py-20 md:py-32" data-testid="groupe-vision">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="flex items-center gap-3 mb-14">
            <span className="diamond" />
            <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
              {t.groupe.vision.eyebrow}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="bg-white border-l-4 border-ekip-copper p-8 md:p-12"
            >
              <span className="text-[10px] uppercase tracking-[0.3em] text-ekip-copper font-medium">
                {t.groupe.vision.visionLabel}
              </span>
              <p className="mt-6 font-display font-semibold text-2xl md:text-3xl leading-[1.25] text-ekip-navy">
                {t.groupe.vision.visionText}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="bg-ekip-navy border-l-4 border-ekip-gold p-8 md:p-12 text-white"
            >
              <span className="text-[10px] uppercase tracking-[0.3em] text-ekip-copper font-medium">
                {t.groupe.vision.missionLabel}
              </span>
              <p className="mt-6 font-display font-semibold text-2xl md:text-3xl leading-[1.25]">
                {t.groupe.vision.missionText}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* History Timeline */}
      <section className="bg-white py-20 md:py-32 border-y border-ekip-gold/20" data-testid="groupe-history">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.groupe.history.eyebrow}
              </span>
            </div>
            <h2 className="col-span-12 md:col-span-8 font-display font-semibold text-3xl md:text-5xl leading-[1.05] text-ekip-navy">
              {t.groupe.history.title}
              <br />
              <em className="italic font-light text-ekip-copper">{t.groupe.history.titleItalic}</em>
            </h2>
          </div>

          <div className="relative pl-6 md:pl-12">
            <div className="absolute left-0 top-0 bottom-0 w-px bg-ekip-gold/40" />
            {t.groupe.history.items.map((it, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="relative pb-14 last:pb-0"
              >
                <span className="timeline-dot absolute -left-[calc(1.5rem+7px)] md:-left-[calc(3rem+7px)] top-1.5" />
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12 md:col-span-3">
                    <span className="font-display font-bold text-3xl md:text-4xl text-ekip-copper">
                      {it.year}
                    </span>
                  </div>
                  <div className="col-span-12 md:col-span-9">
                    <h3 className="font-display font-semibold text-xl md:text-2xl text-ekip-navy">
                      {it.title}
                    </h3>
                    <p className="mt-2 text-ekip-graphite font-body font-light leading-relaxed max-w-2xl">
                      {it.desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Governance */}
      <section className="bg-ekip-cream py-20 md:py-32" data-testid="groupe-governance">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.groupe.governance.eyebrow}
              </span>
            </div>
            <h2 className="col-span-12 md:col-span-8 font-display font-semibold text-3xl md:text-5xl leading-[1.05] text-ekip-navy">
              {t.groupe.governance.title}
              <br />
              <em className="italic font-light text-ekip-copper">{t.groupe.governance.titleItalic}</em>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
            {t.groupe.governance.team.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-white border border-ekip-gold/20 hover:border-ekip-copper transition-colors p-6 md:p-8"
              >
                <div className="w-14 h-14 flex items-center justify-center border border-ekip-copper/40 mb-6" style={{ transform: "rotate(45deg)" }}>
                  <span className="font-display font-bold text-ekip-copper text-lg" style={{ transform: "rotate(-45deg)" }}>
                    {p.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                </div>
                <h3 className="font-display font-semibold text-lg text-ekip-navy">
                  {p.name}
                </h3>
                <span className="mt-1 block text-xs uppercase tracking-[0.24em] text-ekip-copper font-medium">
                  {p.role}
                </span>
                <p className="mt-4 text-sm text-ekip-graphite font-body font-light leading-relaxed">
                  {p.bio}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Governance principles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-ekip-gold/20">
            {t.groupe.governance.principles.map((p, i) => {
              const Icon = PRINCIPLE_ICONS[i];
              return (
                <div key={i} className="p-6 md:p-8 border-r border-b border-ekip-gold/20 bg-white">
                  <div className="w-11 h-11 flex items-center justify-center border border-ekip-copper/50" style={{ transform: "rotate(45deg)" }}>
                    <Icon size={18} strokeWidth={1.4} className="text-ekip-copper" style={{ transform: "rotate(-45deg)" }} />
                  </div>
                  <h4 className="mt-6 font-display font-semibold text-lg text-ekip-navy">
                    {p.title}
                  </h4>
                  <p className="mt-2 text-sm text-ekip-graphite font-body font-light leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key figures */}
      <section className="bg-ekip-navy-deep py-20 md:py-32 border-y border-ekip-gold/20" data-testid="groupe-figures">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.groupe.figures.eyebrow}
              </span>
            </div>
            <h2 className="col-span-12 md:col-span-8 font-display font-semibold text-3xl md:text-5xl leading-[1.05] text-white">
              {t.groupe.figures.title}
              <br />
              <em className="italic font-light text-ekip-copper">{t.groupe.figures.titleItalic}</em>
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-14 gap-x-6">
            {t.groupe.figures.items.map((it, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
              >
                <div className="flex items-baseline gap-1">
                  <span className="font-display font-bold text-5xl md:text-6xl lg:text-7xl text-ekip-copper leading-none">
                    <CountUp end={it.value} duration={2.4} enableScrollSpy scrollSpyOnce separator=" " />
                  </span>
                  <span className="font-display font-bold text-2xl md:text-3xl text-ekip-copper">
                    {it.suffix}
                  </span>
                </div>
                <div className="mt-5 h-px w-10 bg-ekip-copper" />
                <p className="mt-3 text-xs uppercase tracking-[0.22em] text-white/75 font-body font-medium">
                  {it.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
